import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * Donut SVG do sistema (padrão do Dashboard): segmentos proporcionais, total no centro e legenda
 * ao lado com ponto colorido, valor e percentual. Sem biblioteca de gráficos.
 */
export interface DonutSegment {
  label: string;
  value: number;
  color: string;
  /** texto secundário na legenda (ex.: quantidade) */
  sub?: string;
}

interface DonutChartProps {
  segments: DonutSegment[];
  centerValue: string;
  centerLabel?: string;
  formatValue?: (value: number) => string;
  size?: number;
  emptyText?: string;
}

// O furo do donut ocupa ~69% do tamanho (raio 15,915 - metade do traço 3, no viewBox de 42);
// o texto do centro cabe nele com folga de 6px de cada lado.
const HOLE_RATIO = (2 * (15.915 - 1.5)) / 42;
const HOLE_PADDING = 6;
const CHAR_WIDTH_EM = 0.55;

/** Fonte do valor central limitada pela largura do furo: valores longos (R$ 53.111,49) encolhem em vez de vazar. */
const fitFontSize = (text: string, maxWidth: number, base: number): number =>
  Math.max(10, Math.min(base, Math.floor(maxWidth / (Math.max(text.length, 1) * CHAR_WIDTH_EM))));

/** "R$ 53.111,49" vira prefixo pequeno em cima e o número embaixo, para o número ter a largura toda do furo. */
const splitCurrency = (value: string): { prefix?: string; main: string } => {
  const match = /^(R\$)\s+(.+)$/.exec(value.trim());
  return match ? { prefix: match[1], main: match[2] } : { main: value };
};

const DonutChart: React.FC<DonutChartProps> = ({
  segments,
  centerValue,
  centerLabel = "Total",
  formatValue = (v) => String(v),
  size = 120,
  emptyText = "Sem dados no período",
}) => {
  const visible = segments.filter((s) => s.value > 0);
  const total = visible.reduce((sum, s) => sum + s.value, 0);
  let offset = 0;
  const holeWidth = Math.floor(size * HOLE_RATIO) - 2 * HOLE_PADDING;
  const center = splitCurrency(centerValue);
  const centerFontSize = fitFontSize(center.main, holeWidth, size >= 120 ? 15 : 13);

  if (total <= 0) {
    return (
      <Typography sx={{ fontSize: 13, color: "#6c757d", py: 2 }}>{emptyText}</Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox="0 0 42 42" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#e9ecef" strokeWidth="3" />
          {visible.map((seg) => {
            const pct = (seg.value / total) * 100;
            const el = (
              <circle
                key={seg.label}
                cx="21"
                cy="21"
                r="15.915"
                fill="transparent"
                stroke={seg.color}
                strokeWidth="3"
                strokeDasharray={`${pct} ${100 - pct}`}
                strokeDashoffset={-offset}
              />
            );
            offset += pct;
            return el;
          })}
        </svg>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            width: holeWidth,
          }}
        >
          {center.prefix && (
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#6c757d", lineHeight: 1 }}>{center.prefix}</Typography>
          )}
          <Typography sx={{ fontSize: centerFontSize, fontWeight: 700, color: "#2D3748", lineHeight: 1.1, whiteSpace: "nowrap" }}>
            {center.main}
          </Typography>
          <Typography sx={{ fontSize: 10, color: "#6c757d" }}>{centerLabel}</Typography>
        </Box>
      </Box>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, minWidth: 160, flex: 1 }}>
        {visible.map((seg) => (
          <Box component="li" key={seg.label} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: seg.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, flex: 1, minWidth: 0 }} noWrap title={seg.label}>
              {seg.label}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>{formatValue(seg.value)}</Typography>
            <Typography sx={{ fontSize: 11, color: "#6c757d", width: 42, textAlign: "right" }}>
              {((100 * seg.value) / total).toFixed(1)}%
            </Typography>
            {seg.sub && (
              <Typography sx={{ fontSize: 11, color: "#6c757d", whiteSpace: "nowrap" }}>{seg.sub}</Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default DonutChart;
